import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Database,
} from "lucide-react";
import type { ValidationStats } from "@/types/validation";

interface StatsDashboardProps {
  stats: ValidationStats | undefined;
  isLoading?: boolean;
}

export function StatsDashboard({ stats, isLoading }: StatsDashboardProps) {
  if (isLoading || !stats) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium h-4 bg-muted rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-muted rounded w-1/2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const byType =
    stats.by_type && typeof stats.by_type === "object" ? stats.by_type : {};
  const bySource =
    stats.by_source && typeof stats.by_source === "object"
      ? stats.by_source
      : {};
  const byScope =
    stats.by_scope && typeof stats.by_scope === "object" ? stats.by_scope : {};

  const total = Number(stats.total_issues) || 0;
  const errors = Number(stats.error_count) || 0;
  const warnings = Number(stats.warning_count) || 0;
  const unresolved = Number(stats.unresolved_count) || 0;
  const addressed = Number(stats.addressed_count) || 0;

  const topTypes = Object.entries(byType)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Database className="w-4 h-4" />
              Total Issues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{total}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-destructive" />
              Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{errors}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {total > 0 ? Math.round((errors / total) * 100) : 0}% of total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-warning" />
              Warnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{warnings}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {total > 0 ? Math.round((warnings / total) * 100) : 0}% of total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              Unresolved
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{unresolved}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {addressed} addressed
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              Issues by Source
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(bySource).map(([source, count]) => (
                <div key={source} className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground uppercase">
                    {source}
                  </span>
                  <span className="text-sm font-medium">{Number(count)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              Top Validation Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topTypes.length > 0 ? (
              <div className="space-y-2">
                {topTypes.map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground truncate mr-2">
                      {type}
                    </span>
                    <span className="text-sm font-medium">{Number(count)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No validation types
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              Issues by Scope
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(byScope).map(([scope, count]) => (
                <div key={scope} className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground capitalize">
                    {scope}
                  </span>
                  <span className="text-sm font-medium">{Number(count)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
